/**
 * Stripe.js: загрузка библиотеки и канон формы добавления карты.
 *
 * До 16.09 loadStripeJs жил копией в ЧЕТЫРЁХ компонентах, причём двумя
 * редакциями: чекаут успел обзавестись ранним выходом и {once:true},
 * кабинет с синглом остались на первой версии. Здесь оставлена зрелая.
 *
 * Форма карты (SetupIntent) живёт только в ЛК (AccountApp), её конфиг
 * тоже здесь - канон в одном месте. Форма ОПЛАТЫ чекаута сюда НЕ
 * переехала: у неё свой appearance с rules, mode/amount/
 * setupFutureUsage - общего с картой только загрузчик.
 */

const SRC = 'https://js.stripe.com/v3/';

/**
 * Загрузка Stripe.js с ретраями.
 *
 * @param {number} retries сколько раз перевесить скрипт после сбоя
 * @returns {Promise<void>}
 */
export function loadStripeJs( retries = 2 ) {

    if( typeof window.Stripe === 'function' )
        return Promise.resolve();

    return new Promise( ( resolve, reject ) => {

        let script = document.querySelector( 'script[src*="js.stripe.com/v3"]' );

        // скрипт уже в DOM и успел выполниться - ждать нечего
        if( script && typeof window.Stripe === 'function' )
            return resolve();

        const onError = () => {

            script?.remove();

            if( retries > 0 )
                setTimeout( () => loadStripeJs( retries - 1 ).then( resolve, reject ), 600 );
            else
                reject( new Error( 'Failed to load Stripe.js' ) );
        };

        const onLoad = () => {
            if( typeof window.Stripe === 'function' )
                resolve();
            else
                onError();
        };

        if( ! script ) {
            script       = document.createElement( 'script' );
            script.src   = SRC;
            script.async = true;
            document.head.appendChild( script );
        }

        script.addEventListener( 'load',  onLoad,  { once: true } );
        script.addEventListener( 'error', onError, { once: true } );
    });
}

/**
 * Палитра Elements формы карты.
 *
 * Тёмная нужна ТОЛЬКО личному кабинету: там модалка сама тёмная
 * (--modal-bg #111A2B), и светлый элемент Stripe оказывался в ней
 * белым пятном. Чекаут темы не знает - ему светлая.
 *
 * Значения зеркалят core/_variables: текст - --page-text, поле -
 * --form-input-bg (var(--navy) в тёмной), рамка - --form-input-border
 * (slate-900), подпись - slate-400. База 'night' - готовая тёмная тема
 * Stripe, поверх неё правим только то, что расходится с кабинетом.
 *
 * Именно ЗНАЧЕНИЯ, а не var(): элемент живёт в iframe Stripe, куда
 * css-переменные страницы не попадают - var() там не разрешится.
 *
 * @param {'light'|'dark'} theme
 */
export function cardAppearance( theme = 'light' ) {

    const common = {
        colorPrimary : '#FF6B35',
        fontFamily   : '"DM Sans", system-ui, sans-serif',
        fontSizeBase : '14px',
    };

    // --page-text светлой темы = var(--navy)
    if( theme !== 'dark' )
        return {
            theme     : 'stripe',
            variables : { ...common, colorText: '#131C2E' },
        };

    return {
        theme     : 'night',
        variables : {
            ...common,
            colorBackground      : '#131C2E',   // --navy, как --form-input-bg
            colorText            : 'rgba(255,255,255,.9)',   // --page-text тёмной
            colorTextSecondary   : '#94A3B8',   // --slate-400
            colorTextPlaceholder : '#64748B',   // --slate-500
            colorDanger          : '#EF4444',   // --red
        },
        rules : {
            '.Input'  : { border: '1px solid #1E293B' },   // --slate-900
            '.Tab'    : { border: '1px solid #1E293B' },
            '.Label'  : { color: '#94A3B8' },
        },
    };
}

/**
 * Elements + Payment Element под добавление карты (SetupIntent).
 *
 * Только карта: адрес и email не собираем, мандат прячем, кошельки не
 * предлагаем. Link и Bank гасятся ТОЛЬКО в Stripe Dashboard
 * (Settings -> Payments -> Payment methods -> конфигурация -> Link ->
 * Turn off) Канон формы: documentation/notes/stripe_payment_element.md
 *
 * @param {object} stripe       результат window.Stripe(pk)
 * @param {string} clientSecret секрет SetupIntent
 * @param {'light'|'dark'} theme тема окна, в котором живёт форма
 * @returns {{elements: object, paymentElement: object}}
 */
export function createCardElements( stripe, clientSecret, theme = 'light' ) {

    const elements = stripe.elements({
        clientSecret,
        fonts : [{
            cssSrc: 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap',
        }],
        appearance : cardAppearance( theme ),
    });

    const paymentElement = elements.create( 'payment', {
        layout             : 'tabs',
        paymentMethodTypes : ['card'],
        terms              : { card: 'never' },
        fields : {
            billingDetails : {
                email : 'never',
                name  : 'never',   // имя держателя - наше поле над элементом
            },
        },
        wallets : {
            applePay  : 'never',
            googlePay : 'never',
        },
    });

    return { elements, paymentElement };
}

/**
 * Подтверждение SetupIntent. Каждое 'never' в fields выше = обязанность
 * передать поле здесь руками (email + name); address собирает сам
 * элемент, его НЕ передаём.
 *
 * @throws {Error} текстом Stripe, если карта не принята
 * @returns {Promise<object>} setupIntent
 */
export async function confirmCardSetup( stripe, elements, { email, name } ) {

    const result = await stripe.confirmSetup({
        elements,
        redirect      : 'if_required',
        confirmParams : {
            payment_method_data : {
                billing_details : { email, name },
            },
        },
    });

    if( result.error )
        throw new Error( result.error.message );

    return result.setupIntent;
}
